import React, { createContext, useContext, useMemo } from "react";

import { LabelImageQueryProps } from "hooks/Review/useCreateLabel";
import { Disagreement } from "types/disagreements";
import { Posicheck } from "types/imageQueries";
import { LabelValue } from "types/labelValue";

export interface CheckDisagreementResponse {
  disagreementId: string | null;
  isDisagreement: boolean;
  challengedAnswer: string | null;
  referenceAnswer: string | null;
  cameFromIQ: boolean;
  cameFromDisagreement: boolean;
  isDisagreementUndo: boolean;
}

export const DEFAULT_DISAGREEMENT_CHECK_RESPONSE: CheckDisagreementResponse = {
  disagreementId: null,
  isDisagreement: false,
  challengedAnswer: null,
  referenceAnswer: null,
  cameFromIQ: false,
  cameFromDisagreement: false,
  isDisagreementUndo: false,
};

export interface ReviewPageContextProps {
  currentImageQuery: Posicheck | Disagreement | null;
  imageQueryId: string | undefined;
  disagreementCheckResponse: CheckDisagreementResponse;
  setDisagreementCheckResponse: React.Dispatch<React.SetStateAction<CheckDisagreementResponse>>;
  labelImageQuery: (
    props: LabelImageQueryProps,
    shouldRemoveRecentIQ: boolean
  ) => Promise<LabelValue | null | undefined>;
  isLabeling: boolean;
  removeRecentIQ: (id: string, addToHistory?: boolean) => (Posicheck | Disagreement)[];
  startPooling: () => void;
}

const ReviewPageContext = createContext<ReviewPageContextProps | null>(null);

interface ReviewPageProviderProps extends ReviewPageContextProps {
  children: React.ReactNode;
}

export function ReviewPageProvider({
  children,
  currentImageQuery,
  imageQueryId,
  disagreementCheckResponse,
  setDisagreementCheckResponse,
  isLabeling,
  labelImageQuery,
  removeRecentIQ,
  startPooling,
}: ReviewPageProviderProps) {
  const contextValue = useMemo(
    () => ({
      currentImageQuery,
      imageQueryId,
      disagreementCheckResponse,
      setDisagreementCheckResponse,
      isLabeling,
      labelImageQuery,
      removeRecentIQ,
      startPooling,
    }),
    [
      currentImageQuery,
      imageQueryId,
      disagreementCheckResponse,
      setDisagreementCheckResponse,
      isLabeling,
      labelImageQuery,
      removeRecentIQ,
      startPooling,
    ]
  );

  return <ReviewPageContext.Provider value={contextValue}>{children}</ReviewPageContext.Provider>;
}

export function useReviewPage() {
  const context = useContext(ReviewPageContext);

  if (!context) {
    throw new Error("useReviewPage must be used inside of ReviewPageProvider");
  }

  return context;
}
